import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInbox } from '@fortawesome/free-solid-svg-icons';
import axios from '../../axios';
import config from '../../config';
import Conversation from '../Conversation/ConversationForm';
import './Inbox.css';
import { useLanguage } from '../../i18n/LanguageContext';

function Inbox({ onMessageRead }) {
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [isConversationOpen, setIsConversationOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isUnreadLoading, setIsUnreadLoading] = useState(false);
  const [preserveHeader, setPreserveHeader] = useState(false);

  const backend_url = config.backend_url;
  const history = useHistory();
  const location = useLocation();
  const userType = localStorage.getItem('userType');
  const { t } = useLanguage();

  // Check for preserveHeader state in location or URL params
  useEffect(() => {
    // Check if we need to preserve the header (from location state or custom event)
    if (location.state && location.state.preserveHeader) {
      setPreserveHeader(true);
    }
  }, [location]);

  // Add event listener for preserve-inbox-header custom event
  useEffect(() => {
    const handlePreserveHeaderEvent = (event) => {
      if (event.detail && event.detail.preserved) {
        setPreserveHeader(true);
      }
    };

    window.addEventListener('preserve-inbox-header', handlePreserveHeaderEvent);

    return () => {
      window.removeEventListener('preserve-inbox-header', handlePreserveHeaderEvent);
    };
  }, []);

  useEffect(() => {
    fetchConversations();
    // Fetch unread count only once on initial load
    if (!isUnreadLoading) {
      fetchUnreadCount();
    }
  }, []);

  // Replace the existing effect with a simpler approach 
  // that only fetches counts when component mounts or conversation changes
  useEffect(() => {
    // Only fetch unread count when returning from a conversation view
    if (!isConversationOpen && !isUnreadLoading) {
      fetchUnreadCount();
      fetchConversations(); // Refresh conversation list to sync individual unread badges
      
      // Notify parent component to update global unread count (once)
      if (onMessageRead) {
        onMessageRead();
      }
    }
  }, [isConversationOpen]); // Only depend on conversation state changes

  const fetchUnreadCount = async () => {
    if (isUnreadLoading) return;
    
    setIsUnreadLoading(true);
    try {
      const response = await axios.get(`${backend_url}/api/conversations/unread-count`, { 
        withCredentials: true 
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    } finally {
      setIsUnreadLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const userId = localStorage.getItem('userId');
      let url = '';

      if (userType === 'seller') {
        url = `${backend_url}/api/sellers/${userId}/conversations`;
      } else if (userType === 'broker') {
        url = `${backend_url}/api/brokers/${userId}/conversations`;
      }

      if (url) {
        const response = await axios.get(url, { withCredentials: true });
        setConversations(response.data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError(t('conversation.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleConversationClick = (id) => {
    if (id === currentConversationId) {
      setIsConversationOpen(!isConversationOpen);
    } else {
      setCurrentConversationId(id);
      setIsConversationOpen(true);
      
      // When opening a conversation, this may mark messages as read, so notify parent
      if (onMessageRead) {
        onMessageRead();
      }
    }
  };

  // Format broker name to avoid showing "- NULL" or "- null" or empty company names
  const formatBrokerName = (brokerName) => {
    if (!brokerName) return '';
    
    // Check for the literal string "NULL" or empty company name after dash
    if (brokerName.includes(' - NULL') || brokerName.includes(' - null')) {
      return brokerName.split(' - ')[0];
    }
    
    // Check for empty company name (just a dash at the end)
    if (brokerName.endsWith(' - ')) {
      return brokerName.slice(0, -3);
    }
    
    return brokerName;
  };

  const reversedConversations = [...conversations].reverse();

  return (
    <div className="inbox">
      {/* Only show header if conversation is not open */}
      {!isConversationOpen && (
        <>
          <h2><FontAwesomeIcon icon={faInbox} className="inbox-icon" /> {t('conversation.inbox')}</h2>
          <div className="unread-message-count">
            {t('conversation.unread', {
              count: unreadCount,
              word: unreadCount === 1 ? t('conversation.messageSingular') : t('conversation.messagePlural'),
            })}
          </div>
        </>
      )}

      {loading && !isConversationOpen ? (
        <p>{t('conversation.loadingConversations')}</p>
      ) : error && !isConversationOpen ? (
        <p className="error">{error}</p>
      ) : (
        <>
          {isConversationOpen ? (
            <>
              <button 
                onClick={() => setIsConversationOpen(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'var(--blue-4)',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <span style={{ marginRight: '5px' }}>←</span> {t('conversation.back')}
              </button>
              <h2><FontAwesomeIcon icon={faInbox} className="inbox-icon" /> {t('conversation.inbox')}</h2>
              <Conversation conversationId={currentConversationId} hideBackButton={true} onMessageSent={onMessageRead} />
            </>
          ) : (
            <>
              {reversedConversations.length > 0 ? (
                reversedConversations.map((conversation) => {
                  const headline = conversation.first_message
                    ? conversation.first_message.split(' ').slice(0, 20).join(' ') + '...'
                    : t('conversation.noMessagesYet');
                  return (
                    <div
                      className={`conversation-summary ${conversation.has_unread_messages ? 'unread' : ''}`}
                      key={conversation.id}
                      onClick={() => handleConversationClick(conversation.id)}
                    >
                      {conversation.broker_name && (
                        <div className="broker-info-header">
                          <div className="broker-avatar-small">
                            {conversation.broker_profile_image ? (
                              <img 
                                src={`${backend_url}/uploads/${conversation.broker_profile_image}`} 
                                alt={formatBrokerName(conversation.broker_name)}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/placeholder-avatar.png';
                                }}
                              />
                            ) : (
                              <div className="avatar-placeholder">
                                {formatBrokerName(conversation.broker_name).charAt(0)}
                              </div>
                            )}
                          </div>
                          <p className="broker-name-header"><strong>{formatBrokerName(conversation.broker_name)}</strong></p>
                        </div>
                      )}
                      <p className="conversation-headline">{headline}</p>
                      {conversation.has_unread_messages && (
                        <div className="unread-badge">
                          {conversation.unread_count > 0 && <span>{conversation.unread_count}</span>}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p>{t('conversation.noConversations')}</p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Inbox;
